/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React from "react"
import { IComponentProp } from "@cd2/tools"
import { icons } from "../constants/icons"
import { Button, List } from "@cd2/components/build/es6"
import { LinkButton } from "./Buttons/LinkButton"
import { eRoutes } from "../routes"
import { definitions } from "../constants/definitions"

export type IBreadcrumb = (string | ICrumbItem)[]

export interface ICrumbItem {
  icon?: IComponentProp<typeof Button, "leftIcon">
  route?: string
  title: string
}

export function EOSBreadcrumb({ path }: { path: IBreadcrumb }) {
  const crumbItems: {
    [key: string]: {
      route: string
      icon: IComponentProp<typeof Button, "leftIcon">
      title: string
    }
  } = {
    reportEvents: {
      route: eRoutes["/system/reports-events"],
      icon: icons.reportEvents,
      title: "Report Events",
    },
    bomItems: {
      route: eRoutes["/stock/bom-items"],
      icon: icons.bomItems,
      title: "BOM Items",
    },
    kits: {
      route: eRoutes["/stock/kits"],
      icon: icons.kits,
      title: "Quote Sets",
    },
    buyingGroups: {
      route: eRoutes["/stock/buying-groups"],
      icon: icons.buyingGroups,
      title: "Buying Groups",
    },
    pending_picks: {
      route: eRoutes["/warehouse/pending-picks"],
      icon: icons.pendingPicks,
      title: "Pending Picks",
    },
    productMasterTags: {
      route: eRoutes["/stock/tags"],
      icon: icons.tags,
      title: "Tags",
    },
    tags: {
      route: eRoutes["/crm/tags"],
      icon: icons.tags,
      title: "Tags",
    },
    universalPriceBands: {
      route: eRoutes["/stock/universal-price-bands"],
      icon: icons.universalPriceBands,
      title: "Universal Price Bands",
    },
    sharedPriceBands: {
      route: eRoutes["/stock/shared-price-bands"],
      icon: icons.sharedPriceBands,
      title: "Shared Price Bands",
    },
    priceBands: {
      route: eRoutes["/stock/price-bands"],
      icon: icons.universalPriceBands,
      title: "Price Bands",
    },
    bulkEmail: {
      route: eRoutes["/crm/bulk-email"],
      icon: icons.bulkEmail,
      title: "Bulk Emails",
    },
    quotes: { route: eRoutes["/quotes"], icon: icons.quotes, title: "Quotes" },
    goodsReceipts: {
      route: eRoutes["/goods-receipts"],
      icon: icons.orders,
      title: "Goods Receipts",
    },
    orders: { route: eRoutes["/orders"], icon: icons.orders, title: "Orders" },
    deliveries: { route: eRoutes["/deliveries"], icon: icons.deliveries, title: "Deliveries" },
    invoices: { route: eRoutes["/invoices"], icon: icons.invoices, title: "Invoices" },
    addresses: { route: eRoutes["/addresses"], icon: icons.addresses, title: "Addresses" },
    drivers: { route: eRoutes["/drivers"], icon: icons.drivers, title: "Drivers" },
    customers: { route: eRoutes["/customers"], icon: icons.customers, title: "Customers" },
    quoteRequests: { route: eRoutes["/quote-requests"], icon: icons.jobs, title: "Quote Requests" },
    jobs: { route: eRoutes["/jobs"], icon: icons.jobs, title: "Customer Requests" },
    internalTickets: { route: eRoutes["/internal-tickets"], icon: icons.edit, title: "Feedback" },
    crm: { route: eRoutes["/crm"], icon: icons.crm, title: "CRM" },
    leads: { route: eRoutes["/crm/leads"], icon: icons.lineOfBusiness, title: "Leads" },
    orderRequests: {
      route: eRoutes["/order-requests"],
      icon: icons.orderRequests,
      title: "Order Requests",
    },
    orderRequestsIncoming: {
      route: eRoutes["/order-requests/incoming"],
      icon: icons.orderRequests,
      title: "Order Requests (Incoming)",
    },
    orderRequestsOutgoing: {
      route: eRoutes["/order-requests/outgoing"],
      icon: icons.orderRequests,
      title: "Order Requests (Outgoing)",
    },
    purchaseOrders: {
      route: eRoutes["/purchase-orders"],
      icon: icons.purchaseOrders,
      title: "Purchase Orders",
    },
    sagePurchaseOrders: {
      route: eRoutes["/sage-purchase-orders"],
      icon: icons.purchaseOrders,
      title: "Sage Purchase Orders",
    },
    users: {
      route: eRoutes["/manage-branch/internal-users"],
      icon: icons.usersInternal,
      title: "Internal Users",
    },
    products: { route: eRoutes["/stock/products"], icon: icons.products, title: "Products" },
    suppliers: { route: eRoutes["/suppliers"], icon: icons.suppliers, title: "Suppliers" },
    supplierMasters: {
      route: eRoutes["/stock/supplier-masters"],
      icon: icons.suppliers,
      title: "Supplier Masters",
    },
    manageBranch: {
      route: eRoutes["/manage-branch"],
      icon: icons.branches,
      title: "Manage Branch",
    },
    sageImports: {
      route: eRoutes["/system/sage-imports"],
      icon: icons.importerLogs,
      title: "Sage Imports",
    },
    sageExportsBranch: {
      route: eRoutes["/system/sage-exports"],
      icon: icons.orders,
      title: "Sage Exports",
    },
    designGuide: {
      route: eRoutes["/system/design-guide"],
      icon: icons.grid,
      title: "Design Guide",
    },
    systemFeedback: {
      route: eRoutes["/system/feedback"],
      icon: icons.edit,
      title: "Feedback",
    },
    product_master_groups: {
      route: eRoutes["/stock/product-master-groups"],
      title: "Product Master Groups",
      icon: icons.products,
    },
    product_group_masters: {
      route: eRoutes["/stock/product-group-masters"],
      title: "Product Group Masters",
      icon: icons.warehouses,
    },
    product_masters: {
      route: eRoutes["/stock/product-masters"],
      title: "Product Masters",
      icon: icons.products,
    },
    product_groups: {
      route: eRoutes["/stock/product-groups"],
      title: "Product Groups",
      icon: icons.productGroups,
    },
    warehouses: {
      route: eRoutes["/stock/warehouses"],
      icon: icons.warehouses,
      title: "Warehouses",
    },
    categories: {
      route: eRoutes["/stock/categories"],
      title: "Categories",
      icon: icons.categories,
    },
    brands: {
      route: eRoutes["/stock/brands"],
      title: "Brands",
      icon: icons.brands,
    },
    manufacturers: {
      route: eRoutes["/stock/manufacturers"],
      title: "Brands",
      icon: icons.manufacturers,
    },
    stock: {
      route: eRoutes["/stock"],
      title: "Stock",
      icon: icons.stock,
    },
    "slim-stock": {
      route: eRoutes["/stock/slim-stock"],
      title: "Slim Stock",
      icon: icons.stock,
    },
    home: { route: "/", icon: icons.home, title: "Home" },
    system: { route: eRoutes["/system"], icon: icons.settings, title: "System" },
    systemRoles: { route: eRoutes["/system/roles"], icon: icons.roles, title: "Roles" },
    siteBanners: {
      route: eRoutes["/system/site-banners"],
      icon: icons.siteBanner,
      title: "Site Banners",
    },
    linkTooltips: {
      route: eRoutes["/system/link-tooltips"],
      icon: icons.info,
      title: "Link Tooltips",
    },
    previewFeatures: {
      route: eRoutes["/system/preview-features"],
      icon: icons.info,
      title: "Preview Features",
    },
    reports: { route: eRoutes["/reports"], icon: icons.crm, title: "Reports" },
    crmCustomersUnlinked: {
      route: eRoutes["/crm/customers/unlinked"],
      icon: icons.link,
      title: "Unlinked Customers",
    },
    crmCustomers: {
      route: eRoutes["/crm/customers"],
      icon: icons.customers,
      title: "Customers",
    },
    customerLists: {
      route: eRoutes["/crm/customer-lists"],
      icon: icons.customerList,
      title: "Customer Lists",
    },
    orderVarianceReportBranch: {
      route: eRoutes["/reports/variance-report-branch"],
      icon: icons.varianceReport,
      title: "Branch",
    },
    orderVarianceReportUser: {
      route: eRoutes["/reports/variance-report-user"],
      icon: icons.varianceReport,
      title: "Sale Rep",
    },
    orderVarianceReportCustomer: {
      route: eRoutes["/reports/variance-report-customer"],
      icon: icons.varianceReport,
      title: "Customer",
    },
    branchReport: {
      route: eRoutes["/reports/branch"],
      icon: icons.varianceReport,
      title: "Branch",
    },
    branchMonthlyReport: {
      route: eRoutes["/reports/branch-monthly"],
      icon: icons.varianceReport,
      title: "Branch - Monthly",
    },
    averageMarginReport: {
      route: eRoutes["/reports/average-margin"],
      icon: icons.varianceReport,
      title: "Average Margin",
    },
    salesReport: {
      route: eRoutes["/reports/sales"],
      icon: icons.varianceReport,
      title: "Sales",
    },
    salesMonthlyReport: {
      route: eRoutes["/reports/sales-monthly"],
      icon: icons.varianceReport,
      title: "Sales - Monthly",
    },
    systemUsers: {
      route: eRoutes["/system/users"],
      icon: icons.users,
      title: "Users",
    },
    videos: {
      route: eRoutes["/system/videos"],
      icon: icons.videos,
      title: "Videos",
    },
    pendingUsers: {
      route: eRoutes["/customer-support/pending-accounts"],
      icon: icons.users,
      title: "Pending Accounts",
    },
    salesReps: {
      route: eRoutes["/crm/sales-reps"],
      icon: icons.usersInternal,
      title: "Sales Reps",
    },
    customerSupport: {
      route: eRoutes["/customer-support"],
      icon: icons.customerSupport,
      title: "Customer Support",
    },
    customerSupportSettings: {
      route: definitions.supportTicketSettings.indexRoute,
      icon: definitions.supportTicketSettings.icon,
      title: definitions.supportTicketSettings.heading,
    },
    customerSupportTickets: {
      route: definitions.supportTickets.indexRoute,
      icon: definitions.supportTickets.icon,
      title: definitions.supportTickets.heading,
    },
    customerSupportGroups: {
      route: definitions.supportTicketGroups.indexRoute,
      icon: definitions.supportTicketGroups.icon,
      title: definitions.supportTicketGroups.heading,
    },
    customerSupportCustomers: {
      route: definitions.customers.indexRoute,
      icon: definitions.customers.icon,
      title: definitions.customers.heading,
    },
    serialNumbers: {
      route: eRoutes["/stock/serial-numbers"],
      icon: icons.serialNumbers,
      title: "Serial Numbers",
    },
    priceImports: {
      route: eRoutes["/price-imports"],
      icon: icons.importer,
      title: "Price Imports",
    },
    minimumPriceImports: {
      route: eRoutes["/stock/minimum-price-imports"],
      icon: icons.importer,
      title: "Minimum Price Imports",
    },
    deliveryMethodReport: {
      route: eRoutes["/reports/delivery-method"],
      icon: icons.deliveries,
      title: "Delivery Method Report",
    },
    salesAllocationsReport: {
      route: eRoutes["/reports/sales-allocation-report"],
      icon: icons.deliveries,
      title: "Sales Allocation Report",
    },
    lineOfBusiness: {
      route: eRoutes["/stock/lines-of-business"],
      icon: icons.lineOfBusiness,
      title: "Line of Business",
    },
    projects: {
      route: eRoutes["/crm/projects"],
      icon: icons.projects,
      title: "Projects",
    },
    stockTakes: {
      route: eRoutes["/stock/stock-takes"],
      icon: icons.stockTakes,
      title: "Stock Takes",
    },
    customerSlideShow: {
      route: eRoutes["/system/customer-slideshow"],
      icon: icons.customerSlideshow,
      title: "Customer Slideshow",
    },
    tradeApplications: {
      route: eRoutes["/customer-support/trade-applications"],
      icon: icons.tradeApplications,
      title: "Trade Applications",
    },
    returnRequests: {
      route: eRoutes["/customer-support/return-requests"],
      icon: icons.returnRequests,
      title: "Return Requests",
    },
    stockTransfers: {
      route: eRoutes["/stock/stock-transfers"],
      icon: icons.stockTransfers,
      title: "Warehouse Transfers",
    },
    hr: {
      route: eRoutes["/hr"],
      icon: icons.hr,
      title: "HR",
    },
    hrTeams: {
      route: eRoutes["/hr/teams"],
      icon: icons.hr,
      title: "HR Teams",
    },
    probationReview: {
      route: eRoutes["/hr/probation-review"],
      icon: icons.users,
      title: "Probation Review",
    },
    bankHolidays: {
      route: eRoutes["/hr/calendar-dates"],
      icon: icons.holidayRequests,
      title: "Calendar Dates",
    },
    hrManageAbsence: {
      route: eRoutes["/hr/absence-requests"],
      icon: icons.holidayRequests,
      title: "Absence Requests",
    },
    hrManageHoliday: {
      route: eRoutes["/hr/holiday-requests"],
      icon: icons.holidayRequests,
      title: "Holiday Requests",
    },
    hrUserAbsence: {
      route: eRoutes["/hr/requests/absence"],
      icon: icons.holidayRequests,
      title: "Absence Requests",
    },
    hrUserHoliday: {
      route: eRoutes["/hr/requests/holiday"],
      icon: icons.holidayRequests,
      title: "Holiday Requests",
    },
    hrStaffChanges: {
      route: eRoutes["/hr/staff-changes"],
      icon: icons.staffChanges,
      title: "Staff Changes",
    },
    rma: {
      route: eRoutes["/customer-support/rma"],
      icon: icons.returnRequests,
      title: "RMA",
    },
    branchRma: {
      route: eRoutes["/rma"],
      icon: icons.returnRequests,
      title: "RMA",
    },
    invoiceAdjustments: {
      route: eRoutes["/system/finance/invoice-adjustments"],
      icon: icons.returnRequests,
      title: "Manual Invoice Adjustments",
    },
    customerPageBanners: {
      route: eRoutes["/customer-support/customer-page-banners"],
      icon: icons.customerPageBanners,
      title: "Customer Page Banners",
    },
  }

  function itemDisplay(item: string | ICrumbItem) {
    const crumbItem = typeof item === "string" ? crumbItems[item] : item
    if (!crumbItem || !crumbItem.title) {
      return null
    }

    return (
      <LinkButton
        to={crumbItem.route || "#"}
        buttonStyle="text"
        leftIcon={crumbItem.icon}
        extendTheme={{ fontSize: 12, color: "#666", padding: 8 }}
      >
        {crumbItem.title}
      </LinkButton>
    )
  }

  return (
    <div
      css={css`
        margin: -8px;
      `}
    >
      <List size={4} horizontal>
        {itemDisplay("home")}
        {path.map((item, i) => (
          <List key={i} size={4} horizontal>
            <span
              css={css`
                font-size: 12px;
                color: #999;
              `}
            >
              /
            </span>
            {itemDisplay(item)}
          </List>
        ))}
      </List>
    </div>
  )
}
